import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { io } from 'socket.io-client';
import { API_BASE_URL, refrescarTokenSesion } from './api';

import './App.css';
import Header from "./components/Header";
import Footer from "./components/Footer";
import SideNav from "./components/SideNav";
import PrivateRoute from "./components/PrivateRoute";

// Vistas
import Login from "./vistas/Login";
import Home from "./vistas/Home";
import Soporte from "./vistas/Soporte";
import TicketISO from "./vistas/TicketISO";
import SoporteDetalle from "./vistas/SoporteDetalle";
import MantencionPreventiva from "./vistas/MantencionPreventiva";
import InformesCentros from "./vistas/InformesCentros";
import BodegaRetiros from "./vistas/BodegaRetiros";
import InventarioBodega from "./vistas/InventarioBodega";
import RevisionEquipos from "./vistas/RevisionEquipos";
import Rendiciones from "./vistas/Rendiciones";
import Clientes from "./vistas/Clientes";
import Calendario from "./vistas/Calendario";
import HistorialTrabajos from "./vistas/HistorialTrabajos";
import HistorialCentros from "./vistas/HistorialCentros";
import DatosIP from "./vistas/DatosIP";
import ConsultaCentro from "./vistas/ConsultaCentro";
import Usuarios from "./vistas/Usuarios";
import Centros from "./vistas/Centros";
import Tecnicos from "./vistas/Tecnicos";
import RegistrosDocumentos from './vistas/RegistrosDocumentos';
import ArmadoTecnico from "./vistas/ArmadoTecnico";
import AsistenteOperativo from "./vistas/AsistenteOperativo";
import Configuraciones from "./vistas/Configuraciones";

const PAGE_ROUTE_MAP = {
    inicio: "/",
    consulta_centro: "/consulta-centro",
    soporte: "/soporte",
    ticket_iso: "/ticket-iso",
    soporte_detalle: "/soporte/detalle",
    mantencion_preventiva: "/mantencion-preventiva",
    informes_centros: "/informes-centros",
    bodega_retiros: "/bodega-retiros",
    inventario_bodega: "/inventario-bodega",
    revision_equipos: "/revision-equipos",
    rendiciones: "/rendiciones",
    armados: "/armados",
    calendario: "/calendario",
    historial_trabajos: "/historial-trabajos",
    historial_centro: "/historial-centro",
    datos_ip: "/datos-ip",
    clientes: "/clientes",
    centros: "/centros",
    registrosdocumentos: "/registrosdocumentos",
    asistente_operativo: "/asistente-operativo",
    usuarios: "/usuarios",
    tecnicos: "/tecnicos",
    configuraciones: "/configuraciones"
};

const PAGE_PRIORITY = [
    "inicio",
    "consulta_centro",
    "armados",
    "revision_equipos",
    "calendario",
    "datos_ip",
    "soporte",
    "ticket_iso",
    "informes_centros",
    "bodega_retiros",
    "inventario_bodega",
    "historial_trabajos",
    "historial_centro",
    "clientes",
    "centros",
    "registrosdocumentos",
    "asistente_operativo",
    "tecnicos",
    "usuarios",
    "rendiciones",
    "configuraciones"
];

const getDefaultAuthenticatedPath = () => {
    const token = localStorage.getItem('token');
    if (!token) return '/login';
    try {
        const decodedToken = jwtDecode(token);
        const paginas = Array.isArray(decodedToken.paginas) ? decodedToken.paginas : [];
        const ordered = [
            ...PAGE_PRIORITY.filter((key) => paginas.includes(key)),
            ...paginas.filter((key) => !PAGE_PRIORITY.includes(key))
        ];
        for (const key of ordered) {
            const route = PAGE_ROUTE_MAP[key];
            if (route) return route;
        }
    } catch (error) {
        console.error("Error al resolver ruta por defecto:", error);
    }
    return '/consulta-centro';
};

const tokenHasPage = (pageKey) => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const decodedToken = jwtDecode(token);
        const paginas = Array.isArray(decodedToken.paginas) ? decodedToken.paginas : [];
        return paginas.includes(pageKey);
    } catch (error) {
        console.error("Error al revisar paginas del token:", error);
        return false;
    }
};

const getSocketBaseUrl = () => {
    if (!API_BASE_URL) return window.location.origin;
    return String(API_BASE_URL).replace(/\/api\/?$/, '') || window.location.origin;
};

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
    const [authReady, setAuthReady] = useState(!localStorage.getItem('token'));
    const [authVersion, setAuthVersion] = useState(0);
    //const [userRole, setUserRole] = useState(null);

    useEffect(() => {
      const token = localStorage.getItem('token');
      if (token) {
          try {
              const decodedToken = jwtDecode(token);
              console.log("Token decodificado:", decodedToken);
               // Guarda el rol del usuario
          } catch (error) {
              console.error("Error al decodificar el token:", error);
          }
      }
    }, []);

    const handleLoginSuccess = () => {
      const token = localStorage.getItem('token');
      if (token) {
          jwtDecode(token);
          
      }
      setAuthReady(true);
      setAuthVersion((version) => version + 1);
      setIsAuthenticated(true);
    };
  

    const handleLogout = () => {
        localStorage.removeItem('token');
        setAuthReady(true);
        setIsAuthenticated(false);
    };

    useEffect(() => {
        if (!isAuthenticated) {
            setAuthReady(true);
            return;
        }

        let activo = true;
        setAuthReady(false);
        refrescarTokenSesion()
            .then((data) => {
                if (activo && data?.token) {
                    setAuthVersion((version) => version + 1);
                }
            })
            .catch((error) => {
                console.error("Error al refrescar permisos:", error);
                if (error?.response?.status === 401) {
                    localStorage.removeItem('token');
                    if (activo) setIsAuthenticated(false);
                }
            })
            .finally(() => {
                if (activo) setAuthReady(true);
            });

        return () => {
            activo = false;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !authReady) return undefined;

        const socket = io(getSocketBaseUrl(), {
            transports: ["websocket", "polling"],
            reconnection: true,
        });

        const refrescarPermisos = async () => {
            try {
                const data = await refrescarTokenSesion();
                if (data?.token) {
                    setAuthVersion((version) => version + 1);
                }
            } catch (error) {
                console.error("Error al sincronizar permisos de rol:", error);
            }
        };

        socket.on("roles_updated", refrescarPermisos);

        return () => {
            socket.off("roles_updated", refrescarPermisos);
            socket.disconnect();
        };
    }, [isAuthenticated, authReady]);

    return (
        <Router>
            <div className={`wrapper ${!isAuthenticated ? 'wrapper-login' : ''}`}>
                {isAuthenticated && <Header key={`header-${authVersion}`} onLogout={handleLogout} />}
                {isAuthenticated && <SideNav key={`sidenav-${authVersion}`} />}

                <div className="content-wrapper">
                    {isAuthenticated && !authReady ? (
                        <div className="p-4 text-muted">Cargando permisos...</div>
                    ) : (
                    <Routes>
                        <Route path="/login" element={!isAuthenticated ? <Login onLoginSuccess={handleLoginSuccess} /> : <Navigate to={getDefaultAuthenticatedPath()} />} />

                        <Route
                            path="/"
                            element={
                                tokenHasPage("inicio") ? (
                                    <PrivateRoute requiredPage="inicio" allowedRoles={['admin', 'tecnico', 'soporte', 'operaciones', 'finanzas']}>
                                        <Home />
                                    </PrivateRoute>
                                ) : isAuthenticated ? (
                                    <Navigate to={getDefaultAuthenticatedPath()} replace />
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        />
                        {/* Rutas protegidas */}
                        <Route
                            path="/soporte"
                            element={
                              <PrivateRoute requiredPage="soporte" allowedRoles={['admin', 'soporte', 'operaciones']}>
                                    <Soporte />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/soporte/detalle"
                            element={
                              <PrivateRoute requiredPage="soporte_detalle" allowedRoles={['admin', 'soporte', 'operaciones']}>
                                    <SoporteDetalle />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/ticket-iso"
                            element={
                                <PrivateRoute requiredPage="ticket_iso" allowedRoles={['admin']}>
                                    <TicketISO />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/mantencion-preventiva"
                            element={
                              <PrivateRoute requiredPage="mantencion_preventiva" allowedRoles={['admin', 'soporte', 'operaciones']}>
                                    <MantencionPreventiva />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/informes-centros"
                            element={
                              <PrivateRoute requiredPage="informes_centros" allowedRoles={['admin', 'soporte', 'operaciones']}>
                                    <InformesCentros />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/bodega-retiros"
                            element={
                              <PrivateRoute requiredPage="bodega_retiros" allowedRoles={['admin', 'operaciones']}>
                                    <BodegaRetiros />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/inventario-bodega"
                            element={
                              <PrivateRoute requiredPage="inventario_bodega" allowedRoles={['admin', 'operaciones']}>
                                    <InventarioBodega />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/revision-equipos"
                            element={
                              <PrivateRoute requiredPage="revision_equipos" allowedRoles={['admin', 'operaciones', 'soporte']}>
                                    <RevisionEquipos />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/rendiciones"
                            element={
                              <PrivateRoute
                                requiredPage="rendiciones"
                                allowedRoles={['admin']}
                              >
                                    <Rendiciones />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/clientes"
                            element={
                              <PrivateRoute requiredPage="clientes" allowedRoles={['admin', 'operaciones']}>
                                    <Clientes />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/calendario"
                            element={
                              <PrivateRoute requiredPage="calendario" allowedRoles={['admin', 'operaciones', 'soporte']}>
                                    <Calendario />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/historial-trabajos"
                            element={
                              <PrivateRoute requiredPage="historial_trabajos" allowedRoles={['admin', 'operaciones', 'soporte']}>
                                    <HistorialTrabajos />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/historial-centro"
                            element={
                              <PrivateRoute requiredPage="historial_centro" allowedRoles={['admin','finanzas', 'operaciones']}>
                                    <HistorialCentros />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/datos-ip"
                            element={
                              <PrivateRoute requiredPage="datos_ip" allowedRoles={['admin', 'operaciones', 'soporte']}>
                                    <DatosIP />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/consulta-centro"
                            element={
                              <PrivateRoute requiredPage="consulta_centro" allowedRoles={['admin', 'tecnico', 'soporte', 'operaciones', 'finanzas']}>
                                    <ConsultaCentro />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/usuarios"
                            element={
                              <PrivateRoute requiredPage="usuarios" allowedRoles={['admin']}>
                                    <Usuarios />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/centros"
                            element={
                              <PrivateRoute requiredPage="centros" allowedRoles={['admin', 'operaciones']}>
                                    <Centros />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tecnicos"
                            element={
                              <PrivateRoute requiredPage="tecnicos" allowedRoles={['admin', 'operaciones']}>
                                    <Tecnicos />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/registrosdocumentos"
                            element={
                              <PrivateRoute requiredPage="registrosdocumentos" allowedRoles={['admin', 'operaciones']}>
                                    <RegistrosDocumentos />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/armados"
                            element={
                              <PrivateRoute requiredPage="armados" allowedRoles={['admin', 'operaciones', 'tecnico']}>
                                    <ArmadoTecnico />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/asistente-operativo"
                            element={
                              <PrivateRoute requiredPage="asistente_operativo" allowedRoles={['admin']} enforceAllowedRoles>
                                    <AsistenteOperativo />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/configuraciones"
                            element={
                              <PrivateRoute requiredPage="configuraciones" allowedRoles={['admin']}>
                                    <Configuraciones />
                                </PrivateRoute>
                            }
                        />

                        <Route path="*" element={<Navigate to={isAuthenticated ? getDefaultAuthenticatedPath() : "/login"} replace />} />
                    </Routes>
                    )}
                </div>

                {isAuthenticated && <Footer />}
            </div>
        </Router>
    );
}

export default App;
